import React from 'react';
import './index.scss';
import { TFunction, useTranslation } from 'react-i18next';
import { Grid, Table } from 'semantic-ui/semantic-ui-imports';
import { ResultSet } from 'services/results';
import { useData } from '../../../contexts/data';
import {
  Aggregate,
  TechnicalSpecification,
  TechnicalSpecificationCategory,
} from '../../../Types/Aggregates';

interface Props {
  data: ResultSet | undefined;
}

export const parseTechnicalSpecifications = (
  activeAggregate: Aggregate,
  data: ResultSet | undefined,
  t: TFunction<'translation', undefined>
): TechnicalSpecificationCategory[] => {
  const cats = activeAggregate?.technical_specification_categories || [];
  const list = activeAggregate?.technical_specifications || [];

  if (!cats?.length || !list?.length) {
    return [];
  }
  let sortedCats = cats;
  const sortedlist = list;

  sortedCats = sortedCats.sort((a, b) =>
    a.tech_spech_category_order < b.tech_spech_category_order ? -1 : 1
  );

  sortedCats = sortedCats.map((c) => {
    // Find only specifications matching with this category:
    let filtered: TechnicalSpecification[] | undefined = sortedlist?.filter(
      (l) => l.category === c.id
    );

    let { name } = c;

    if (filtered) {
      // Specifications found. Prepare category to be included in results
      switch (name) {
        // Override untranslated category names until we fix it in backend
        case 'Aggregat Data':
          name = t(`resultsPage.technicalSpecsPage.catAggregateData`);
          break;
        case 'Ventilator Data':
          name = t(`resultsPage.technicalSpecsPage.catFanData`);
          break;
        case 'Kompressor Data':
          name = t(`resultsPage.technicalSpecsPage.catCompressorData`);
          break;
        case 'Eftervarmeflade Data':
          name = t(`resultsPage.technicalSpecsPage.catReheaterData`);
          break;
        case 'Ecodesign Data':
          name = t(`resultsPage.technicalSpecsPage.catEcodesignData`);
          break;
        default:
          break;
      }

      // Individual specifications sorted within category
      filtered = filtered.sort((a, b) =>
        a.tech_spech_order < b.tech_spech_order ? -1 : 1
      );

      // read correct vals
      filtered = filtered
        .map((ts) => {
          let value = ts.key ? null : ts.value;
          if (!value) {
            try {
              const additionals = data?.additional;
              if (additionals) {
                if (ts.key in data.additional) {
                  value = data.additional[ts.key];
                }
              }
            } catch (err) {
              // console.error(err);
            }
          }
          return { ...ts, value } as TechnicalSpecification;
        })
        .filter((item) => item.value !== null);

      return { ...c, name, technical_specifications: filtered };
    }
    return c;
  });

  return sortedCats;
};

function TechnicalSpecs({ data }: Props) {
  // translation
  const { t } = useTranslation();
  const { activeAggregate } = useData();

  const [categories, setCategories] = React.useState<
    TechnicalSpecificationCategory[]
  >([]);

  React.useEffect(() => {
    if (activeAggregate) {
      setCategories(
        parseTechnicalSpecifications(activeAggregate, data, t) || []
      );
    }
  }, [activeAggregate]);

  return (
    <div className="technical-specs">
      <Grid>
        <Grid.GridRow>
          <h2 className="technical-specs__header">
            {t(`resultsPage.technicalSpecsPage.header`)}
          </h2>
        </Grid.GridRow>
      </Grid>

      {!!categories?.length &&
        categories.map(
          (c) =>
            !!c?.technical_specifications?.length && (
              <Grid key={c.tech_spech_category_order}>
                <Grid.GridRow>
                  <h3 className="technical-specs__subheader">{c.name}</h3>
                </Grid.GridRow>
                {!!c?.technical_specifications?.length && (
                  <Grid.GridRow>
                    <Table textAlign="left" className="technical-specs__table">
                      <Table.Body>
                        {!!c?.technical_specifications?.length &&
                          c.technical_specifications.map((ts) => (
                            <Table.Row key={ts.tech_spech_order}>
                              <Table.Cell>{ts.name}</Table.Cell>
                              <Table.Cell>{ts.value}</Table.Cell>
                            </Table.Row>
                          ))}
                      </Table.Body>
                    </Table>
                  </Grid.GridRow>
                )}
              </Grid>
            )
        )}
    </div>
  );
}

export default TechnicalSpecs;
